import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Lock, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { useAuth, formatApiErrorDetail } from '../lib/AuthContext';

export default function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      await login(email.trim(), password);
      const target = location.state?.from || '/admin';
      navigate(target, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || 'Login failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0d1829] px-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-blue-600/10 border border-blue-500/20 mb-4">
            <Lock className="w-6 h-6 text-blue-400" />
          </div>
          <h1 className="text-2xl font-semibold text-white" data-testid="admin-login-title">
            Engineer Sign-In
          </h1>
          <p className="text-sm text-slate-400 mt-1">Deep Control · Service Portal</p>
        </div>

        <form
          onSubmit={handleSubmit}
          className="bg-[#1e2d4a]/40 border border-white/10 rounded-2xl p-6 space-y-4 backdrop-blur"
          data-testid="admin-login-form"
        >
          <div className="space-y-2">
            <Label htmlFor="login-email" className="text-slate-300 text-xs uppercase tracking-wider">
              Email
            </Label>
            <Input
              id="login-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="bg-[#0d1829] border-white/10 text-white"
              data-testid="admin-login-email-input"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="login-password" className="text-slate-300 text-xs uppercase tracking-wider">
              Password
            </Label>
            <Input
              id="login-password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-[#0d1829] border-white/10 text-white"
              data-testid="admin-login-password-input"
            />
          </div>

          {error && (
            <div className="text-sm text-red-400 bg-red-500/5 border border-red-500/20 rounded-md px-3 py-2" data-testid="admin-login-error">
              {error}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white"
            data-testid="admin-login-submit-button"
          >
            {busy ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…
              </>
            ) : (
              'Sign in'
            )}
          </Button>
        </form>

        <p className="text-center text-xs text-slate-500 mt-6">
          Secure session · cookies auto-expire after 8 hours of inactivity
        </p>
      </div>
    </div>
  );
}
